import { useMemo, useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, query, where, orderBy, onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Block, DailyMetrics, WeeklyMetrics, DailyMIT, RevisionLog } from '@/types';
import { useAtomValue } from 'jotai';
import { firebaseUserAtom } from '@/stores/auth';
import { queryKeys } from '@/lib/queryKeys';
import {
  parseWeekId,
  formatDateKey,
  getWeekDateKeys,
  calculateDailyMetricsFromBlocks,
  createEmptyDailyMetrics,
  calculateWeeklyMetrics,
  formatDuration,
  getCurrentWeekId,
} from '@/services/metrics.service';

// ============================================
// Types
// ============================================

export interface UseDailyMetricsResult {
  metrics: DailyMetrics;
  isLoading: boolean;
  error: Error | null;
}

export interface UseWeeklyMetricsResult {
  metrics: WeeklyMetrics;
  isLoading: boolean;
  error: Error | null;
}

// ============================================
// Daily Metrics Hook
// ============================================

export function useDailyMetrics(date: string): UseDailyMetricsResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();

  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = queryKeys.metrics.daily(userId, date);
  const cacheKey = `${userId}-${date}-metrics`;

  // Use React Query for caching and state management
  const {
    data: metrics = createEmptyDailyMetrics(date),
    isLoading: _queryLoading,
    error,
  } = useQuery<DailyMetrics, Error>({
    queryKey,
    queryFn: async () => createEmptyDailyMetrics(date),
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Set up real-time Firestore listeners for blocks and day data
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;
    let blocksData: Block[] = [];
    let revisionCount = 0;
    let mitsCompleted = 0;
    let mitsTotal = 0;
    let blocksInitialized = false;
    let dayInitialized = false;

    const updateMetrics = () => {
      if (isCleanedUp) return;
      if (!blocksInitialized || !dayInitialized) return;

      const calculatedMetrics = calculateDailyMetricsFromBlocks(
        date,
        blocksData,
        revisionCount,
        mitsCompleted,
        mitsTotal
      );

      queryClient.setQueryData(queryKey, calculatedMetrics);
      setIsListenerInitialized(true);
    };

    // Timeout fallback
    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Daily metrics listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 3000);

    // Listen to blocks
    const blocksRef = collection(db, 'users', userId, 'blocks');
    const blocksQuery = query(blocksRef, where('date', '==', date), orderBy('startTime', 'asc'));

    const unsubscribeBlocks = onSnapshot(
      blocksQuery,
      (snapshot) => {
        if (isCleanedUp) return;
        blocksData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Block[];
        blocksInitialized = true;
        updateMetrics();
      },
      (err) => {
        console.error('Error listening to blocks for metrics:', err);
        if (!isCleanedUp) {
          blocksInitialized = true;
          updateMetrics();
        }
      }
    );

    // Listen to day document for revisions and MITs
    const dayRef = doc(db, 'users', userId, 'days', date);
    const unsubscribeDay = onSnapshot(
      dayRef,
      (snapshot) => {
        if (isCleanedUp) return;
        const dayData = snapshot.data();
        const revisions: RevisionLog[] = dayData?.revisionLogs || [];
        const mits: DailyMIT[] = dayData?.mits || [];

        revisionCount = revisions.length;
        mitsTotal = mits.length;
        mitsCompleted = mits.filter((mit) => mit.completed).length;
        dayInitialized = true;
        updateMetrics();
      },
      (err) => {
        console.error('Error listening to day for metrics:', err);
        if (!isCleanedUp) {
          dayInitialized = true;
          updateMetrics();
        }
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribeBlocks();
      unsubscribeDay();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Compute actual loading state
  const isLoading = !!userId && !isListenerInitialized;

  return {
    metrics,
    isLoading,
    error: error ?? null,
  };
}

// ============================================
// Weekly Metrics Hook
// ============================================

export function useWeeklyMetrics(weekId: string): UseWeeklyMetricsResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();

  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = queryKeys.metrics.weekly(userId, weekId);
  const cacheKey = `${userId}-${weekId}-weekly-metrics`;

  // Parse week dates
  const { weekStart, weekEnd } = useMemo(() => {
    try {
      return parseWeekId(weekId);
    } catch {
      // Return current week as fallback
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { weekStart: monday, weekEnd: sunday };
    }
  }, [weekId]);

  const dateKeys = useMemo(() => getWeekDateKeys(weekStart), [weekStart]);

  // Create empty weekly metrics using service function
  const createEmptyWeeklyMetrics = (): WeeklyMetrics => {
    const emptyDailyBreakdown = dateKeys.map(createEmptyDailyMetrics);
    return calculateWeeklyMetrics(weekId, emptyDailyBreakdown, weekStart, weekEnd);
  };

  // Use React Query for caching
  const {
    data: metrics = createEmptyWeeklyMetrics(),
    isLoading: _queryLoading,
    error,
  } = useQuery<WeeklyMetrics, Error>({
    queryKey,
    queryFn: async () => createEmptyWeeklyMetrics(),
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Set up listeners for all days in the week
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;
    const blocksByDate = new Map<string, Block[]>();
    const dayDataByDate = new Map<
      string,
      { revisionCount: number; mitsCompleted: number; mitsTotal: number }
    >();
    const initializedDates = new Set<string>();

    const updateWeeklyMetrics = () => {
      if (isCleanedUp) return;
      if (initializedDates.size < dateKeys.length * 2) return; // Wait for all listeners

      // Calculate daily metrics
      const dailyBreakdown: DailyMetrics[] = dateKeys.map((dateKey) => {
        const blocks = blocksByDate.get(dateKey) || [];
        const dayData = dayDataByDate.get(dateKey) || {
          revisionCount: 0,
          mitsCompleted: 0,
          mitsTotal: 0,
        };
        return calculateDailyMetricsFromBlocks(
          dateKey,
          blocks,
          dayData.revisionCount,
          dayData.mitsCompleted,
          dayData.mitsTotal
        );
      });

      // Use service function to calculate weekly metrics
      const weeklyMetrics = calculateWeeklyMetrics(weekId, dailyBreakdown, weekStart, weekEnd);

      queryClient.setQueryData(queryKey, weeklyMetrics);
      setIsListenerInitialized(true);
    };

    // Timeout fallback
    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Weekly metrics listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 5000);

    const unsubscribes: (() => void)[] = [];

    // Set up listeners for each date
    for (const dateKey of dateKeys) {
      // Listen to blocks for this date
      const blocksRef = collection(db, 'users', userId, 'blocks');
      const blocksQuery = query(
        blocksRef,
        where('date', '==', dateKey),
        orderBy('startTime', 'asc')
      );

      const unsubscribeBlocks = onSnapshot(
        blocksQuery,
        (snapshot) => {
          if (isCleanedUp) return;
          const blocks = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Block[];
          blocksByDate.set(dateKey, blocks);
          initializedDates.add(`blocks-${dateKey}`);
          updateWeeklyMetrics();
        },
        (err) => {
          console.error(`Error listening to blocks for ${dateKey}:`, err);
          if (!isCleanedUp) {
            initializedDates.add(`blocks-${dateKey}`);
            updateWeeklyMetrics();
          }
        }
      );
      unsubscribes.push(unsubscribeBlocks);

      // Listen to day document for this date
      const dayRef = doc(db, 'users', userId, 'days', dateKey);
      const unsubscribeDay = onSnapshot(
        dayRef,
        (snapshot) => {
          if (isCleanedUp) return;
          const dayData = snapshot.data();
          const revisions: RevisionLog[] = dayData?.revisionLogs || [];
          const mits: DailyMIT[] = dayData?.mits || [];

          dayDataByDate.set(dateKey, {
            revisionCount: revisions.length,
            mitsTotal: mits.length,
            mitsCompleted: mits.filter((mit) => mit.completed).length,
          });
          initializedDates.add(`day-${dateKey}`);
          updateWeeklyMetrics();
        },
        (err) => {
          console.error(`Error listening to day for ${dateKey}:`, err);
          if (!isCleanedUp) {
            dayDataByDate.set(dateKey, { revisionCount: 0, mitsCompleted: 0, mitsTotal: 0 });
            initializedDates.add(`day-${dateKey}`);
            updateWeeklyMetrics();
          }
        }
      );
      unsubscribes.push(unsubscribeDay);
    }

    // Also listen to weekly plan for goals
    const weeklyPlanRef = doc(db, 'users', userId, 'weeklyPlans', weekId);
    const unsubscribeWeeklyPlan = onSnapshot(
      weeklyPlanRef,
      (snapshot) => {
        if (isCleanedUp) return;
        if (snapshot.exists()) {
          const planData = snapshot.data();
          const goals = planData?.goals || [];
          const goalsTotal = goals.length;
          const goalsCompleted = goals.filter((g: { completed: boolean }) => g.completed).length;

          // Update the current metrics with goals data
          const currentMetrics = queryClient.getQueryData<WeeklyMetrics>(queryKey);
          if (currentMetrics) {
            queryClient.setQueryData(queryKey, {
              ...currentMetrics,
              goalsTotal,
              goalsCompleted,
            });
          }
        }
      },
      (err) => {
        // Weekly plan might not exist yet - that's OK
        if (err.code !== 'not-found') {
          console.error('Error listening to weekly plan:', err);
        }
      }
    );
    unsubscribes.push(unsubscribeWeeklyPlan);

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, dateKeys.join(',')]);

  // Compute actual loading state
  const isLoading = !!userId && !isListenerInitialized;

  return {
    metrics,
    isLoading,
    error: error ?? null,
  };
}

// ============================================
// Re-export utilities from service
// ============================================

export { formatDuration, getCurrentWeekId };
